import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { DebouncedFunc } from 'lodash';
import isNil from 'lodash/isNil';

import { AnalyticsEvent } from './analyticsTypes';
import { featureFlags } from 'featureFlags';
import { UserParams } from '@segment/analytics-next/dist/pkg/core/arguments-resolver';

export const debouncedEvent = (
  event: AnalyticsEvent | string,
  properties?: undefined | Record<string, unknown>
): DebouncedFunc<() => void> =>
  debounce(() => {
    trackEvent(event, properties);
  }, 250);

export const trackEvent = (
  event: AnalyticsEvent | string,
  properties?: undefined | Record<string, unknown>
): void => {
  if (!window.analytics || process.env.REACT_APP_FE_ENV !== 'prod') {
    // eslint-disable-next-line no-console
    console.warn('Would have called analytics with', event, properties);
    return;
  }

  const user = typeof window.analytics.user === 'function' && window.analytics.user();
  const userTraits = user ? user.traits() : {};

  const companyId = get(userTraits, 'company.companyId');

  window.analytics.track(
    event,
    {
      ...properties,
      featureFlags: featureFlags.getAllCachedTreatments(),
    },
    {
      // https://segment.com/docs/connections/spec/common/#context
      context: {
        groupId: companyId,
      },
    }
  );
};

export const identify = (...args: UserParams) => {
  const [id, traits, options, callback] = args;
  if (!window.analytics || process.env.REACT_APP_FE_ENV !== 'prod') {
    // eslint-disable-next-line no-console
    console.warn('Would have called analytics with', id, traits, options, callback);
    return;
  }
  if (isNil(id)) {
    // eslint-disable-next-line no-console
    console.warn('Would have called identify with a NIL id -- exiting early');
    return;
  }

  window.analytics && window.analytics.identify(id, traits, options, callback);
};
